/*
 * <rental-availability>
 *
 * Renders rental availability for the current variant and caps the product
 * page quantity selector at the number of units actually on hand.
 *
 * It plugs into Spotlight's existing product architecture rather than
 * replacing it: <product-info> already re-fetches the section markup on every
 * variant change and publishes PUB_SUB_EVENTS.variantChange with the parsed
 * document, so this element simply swaps in its own freshly rendered markup
 * from that document. No extra network requests are made.
 */

if (!customElements.get('rental-availability')) {
  customElements.define(
    'rental-availability',
    class RentalAvailability extends HTMLElement {
      constructor() {
        super();
        this.variantChangeUnsubscriber = undefined;
      }

      connectedCallback() {
        this.applyQuantityCap();

        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );
      }

      disconnectedCallback() {
        if (typeof this.variantChangeUnsubscriber === 'function') this.variantChangeUnsubscriber();
      }

      get productInfo() {
        return this.closest('product-info');
      }

      get status() {
        return this.querySelector('[data-rental-status]');
      }

      get quantityInput() {
        return this.productInfo?.querySelector('.quantity__input');
      }

      onVariantChange(event) {
        const data = event?.data;
        if (!data || !data.html) return;

        const productInfo = this.productInfo;
        if (!productInfo) return;

        const ownSectionId = productInfo.dataset.originalSection || productInfo.dataset.section;
        if (data.sectionId && ownSectionId && data.sectionId !== ownSectionId) return;

        const source = data.html.querySelector('rental-availability [data-rental-status]');
        const current = this.status;

        if (source && current) {
          current.replaceWith(source.cloneNode(true));
        }

        const sourceRoot = data.html.querySelector('rental-availability');
        if (sourceRoot) {
          this.className = sourceRoot.className;
        }

        // Run after <product-info> has finished re-applying its own quantity rules.
        window.requestAnimationFrame(() => this.applyQuantityCap());
      }

      /*
       * Caps the quantity selector at the units on hand for the selected
       * variant, without disturbing any quantity rules the merchant has set.
       * The tighter of the two limits always wins.
       */
      applyQuantityCap() {
        const input = this.quantityInput;
        const status = this.status;
        if (!input || !status) return;

        const raw = status.dataset.rentalMax;
        const inventoryMax = raw === undefined || raw === '' ? null : parseInt(raw, 10);
        if (inventoryMax === null || Number.isNaN(inventoryMax)) return;

        const ruleMax = input.dataset.max ? parseInt(input.dataset.max, 10) : null;
        const cap = ruleMax === null || Number.isNaN(ruleMax) ? inventoryMax : Math.min(ruleMax, inventoryMax);

        if (cap <= 0) return;

        input.dataset.max = cap;
        input.max = cap;

        if (parseInt(input.value, 10) > cap) input.value = cap;

        // Let Spotlight's own <quantity-input> re-evaluate its +/- buttons.
        const quantityInputElement = input.closest('quantity-input');
        if (quantityInputElement && typeof quantityInputElement.validateQtyRules === 'function') {
          quantityInputElement.validateQtyRules();
        }
      }
    }
  );
}
